/* Anniversary popup. Optional native Shopify email form; no fetch/XHR, cart writes or coupon URLs. */
(() => {
  'use strict';
  const TARGET = '189656367183';
  const KEY = 'fadlash:anniversary-popup:v1:' + TARGET;
  const VISIT = 30 * 60 * 1000;
  const DAY = 86400000;
  const kinds = ['general', 'pro', 'diy'];
  const rules = {
    inWindow(start, end, now) {
      // Require explicit timezone; incomplete/invalid dates must not enable automatic display.
      const parse = value => !value ? null : /T.*(?:Z|[+-]\d\d:\d\d)$/.test(value) ? Date.parse(value) : NaN;
      const a = parse(start), b = parse(end);
      return (a === null || Number.isFinite(a)) && (b === null || Number.isFinite(b)) &&
        !(a !== null && b !== null && a >= b) && (a === null || now >= a) && (b === null || now < b);
    },
    canAuto(s) {
      return rules.autoReason(s) === 'ready';
    },
    autoReason(s) {
      if (!s.target) return 'target';
      if (!s.enabled) return 'auto_off';
      if (!s.reviewed) return 'unreviewed';
      if (!s.storage) return s.storageReason || 'storage_failed';
      if (!s.pageAllowed) return 'excluded';
      if (!s.inWindow) return 'schedule';
      if (s.blocked) return s.blockReason || 'blocked';
      if (s.checkout) return 'checkout';
      if (s.shown) return 'shown';
      if (s.cooling) return 'cooldown';
      const scrollRequired = s.requireScroll !== false;
      if (scrollRequired && !(s.scrollRange >= Math.max(200, s.height * .5))) return 'short_page';
      const timeReady = s.activeMs >= s.delay * 1000;
      const scrollReady = !scrollRequired || s.scrollRatio >= s.threshold / 100;
      if (!timeReady && !scrollReady) return 'waiting_both';
      if (!timeReady) return scrollRequired ? 'waiting_time' : 'waiting_time_only';
      if (!scrollReady) return 'waiting_scroll';
      return 'ready';
    },
    safeHref(raw, origin, root) {
      try {
        const url = new URL(raw, origin);
        if (url.origin !== origin || !['http:', 'https:'].includes(url.protocol)) return null;
        const decoded = decodeURIComponent(url.pathname).toLowerCase();
        const base = root === '/' ? '' : root.replace(/\/$/, '');
        const relative = decoded.startsWith(base + '/') ? decoded.slice(base.length) : decoded;
        if (!/^\/(?:$|pages\/|collections\/|products\/|blogs\/)/.test(relative)) return null;
        // Only navigational paths are allowed. Discard all query parameters, including codes/redirects.
        url.search = typeof window !== 'undefined' && window.Shopify?.theme?.role === 'unpublished' ? '?preview_theme_id=' + TARGET : '';
        return url.href;
      } catch (_) { return null; }
    },
    emailReturn(raw, kind, formId, origin, root) {
      let source;
      try { source = new URL(raw, origin); } catch (_) { return null; }
      if (source.origin !== origin) return null;
      // Validation can render at /contact; send a corrected attempt back to the home draft.
      const safe = rules.safeHref(raw, origin, root) || rules.safeHref(root || '/', origin, root);
      if (!safe || !kinds.includes(kind) || !/^fp-email-form-[a-zA-Z0-9_-]+$/.test(formId)) return null;
      const url = new URL(safe);
      url.searchParams.set('fadlash_email_return', kind);
      const review = source.searchParams.get('fadlash_popup_review');
      if (typeof window !== 'undefined' && window.Shopify?.designMode && kinds.includes(review)) url.searchParams.set('fadlash_popup_review', review);
      url.hash = formId;
      return url.pathname + url.search + url.hash;
    },
    state(raw, now) {
      let data = {};
      try { data = JSON.parse(raw || '{}'); } catch (_) { /* Ignore corrupt own record. */ }
      if (!data || typeof data !== 'object') data = {};
      const validVisit = Number.isFinite(data.lastSeen) && now >= data.lastSeen && now - data.lastSeen < VISIT;
      return { lastSeen: now, shown: validVisit && data.shown === true,
        closedUntil: Number.isFinite(data.closedUntil) ? data.closedUntil : 0,
        checkoutUntil: Number.isFinite(data.checkoutUntil) ? data.checkoutUntil : 0 };
    }
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { rules, KEY, TARGET, VISIT, DAY };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('fadlash-promo-popup')) return;

  class FadlashPromoPopup extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      const theme = window.Shopify?.theme;
      if (String(theme?.id) !== TARGET || !this.dialog?.showModal) return;
      this.hidden = false;
      this.review = this.dataset.reviewEnabled === 'true' &&
        window.Shopify.designMode;
      this.querySelector('[data-fp-review]').hidden = !this.review;
      this.status = this.querySelector('[data-fp-status]');
      this.progress = this.querySelector('[data-fp-progress]');
      this.feedback = this.querySelector('[data-fp-feedback]');
      this.activeMs = 0; this.lastTick = performance.now(); this.pageShown = false; this.checkingOut = false;
      this.inputUntil = 0; this.lastTouch = 0;
      const nativeReturnKind = kinds.find(kind => {
        const form = this.querySelector('template[data-fp-panel="' + kind + '"]')?.content.querySelector('[data-fp-signup]');
        return form && location.hash === '#' + form.id && form.querySelector('[data-fp-email-feedback]');
      });
      const returnKind = new URL(location.href).searchParams.get('fadlash_email_return') || nativeReturnKind;
      this.emailReturnKind = kinds.includes(returnKind) && this.dataset.emailEnabled === 'true' ? returnKind : null;
      this.on(this, 'click', e => {
        const opener = e.target.closest('[data-fp-open]');
        if (opener) this.open(opener.dataset.fpOpen, opener);
        if (e.target.closest('[data-fp-close]')) this.close('dismiss');
        if (e.target.closest('[data-fp-reset]') && this.review) this.reset();
        if (e.target.closest('[data-fp-cta]')) this.close('cta');
      });
      this.on(this.dialog, 'cancel', e => { e.preventDefault(); this.close('dismiss'); });
      this.on(this.dialog, 'close', () => this.release());
      this.on(this.dialog, 'click', e => { if (e.target === this.dialog) this.close('dismiss'); });
      this.on(this.dialog, 'keydown', e => this.trap(e));
      this.on(this, 'focusin', e => { const form = e.target.closest('[data-fp-signup]'); if (form) this.protectEmailForm(form); });
      this.on(this, 'submit', e => {
        const form = e.target.closest('[data-fp-signup]');
        if (!form) return;
        // This UI checks explicit consent; native Shopify handles spam protection and its subscription response.
        // Never send or retain the email in popup frequency storage.
        if (this.dataset.emailEnabled !== 'true' || window.Shopify.designMode || !form.checkValidity()) {
          e.preventDefault(); form.reportValidity();
        }
      });
      this.on(document, 'focusin', e => { if (!this.contains(e.target) && this.isInput(e.target)) this.inputUntil = Date.now() + 15000; });
      this.on(document, 'input', e => { if (!this.contains(e.target)) this.inputUntil = Date.now() + 15000; });
      this.on(document, 'click', e => {
        const node = e.target.closest('a,button,input');
        if (!node || this.contains(node)) return;
        const href = node.getAttribute('href') || '';
        if (/\/(checkouts?|wallets)(\/|\?|$)/i.test(href) || node.name === 'checkout' || node.closest('.shopify-payment-button,[data-shopify="payment-button"]')) this.stopCheckout();
      }, { capture: true });
      this.on(document, 'submit', e => { if (e.submitter?.name === 'checkout' || /\/checkouts?(\/|\?|$)/i.test(e.target.action || '')) this.stopCheckout(); }, { capture: true });
      this.on(window, 'pagehide', () => { if (this.dialog.open) this.close('leave'); });
      this.on(window, 'pageshow', () => { this.lastTick = performance.now(); });
      this.on(document, 'visibilitychange', () => { this.lastTick = performance.now(); });
      this.on(document, 'visitorConsentCollected', () => this.pulse());
      this.on(window, 'storage', e => {
        // Other tabs update lastSeen routinely. Do not discard an in-progress form.
        // The current pulse reads frequency state; only checkout must interrupt an open dialog.
        if (e.key === KEY && rules.state(e.newValue, Date.now()).checkoutUntil > Date.now()) {
          this.checkingOut = true; this.close('checkout');
        }
      });
      // Read the existing privacy decision only; never request or set consent.
      if (!window.Shopify.customerPrivacy && window.Shopify.loadFeatures) {
        this.privacyLoading = true;
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
            this.privacyLoading = false; this.privacyLoadError = !!error; this.pulse();
          });
        } catch (_) { this.privacyLoading = false; this.privacyLoadError = true; }
      }
      this.timer = setInterval(() => this.pulse(), 1000);
      this.observer = new MutationObserver(() => { if (this.dialog.open && this.overlay()) this.close('conflict'); });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-modal'] });
      this.pulse();
    }
    disconnectedCallback() {
      clearInterval(this.timer); this.abort?.abort(); this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.release();
    }
    on(node, name, fn, options = {}) { node.addEventListener(name, fn, { ...options, signal: this.abort.signal }); }
    setStatus(text) { if (this.review && this.feedback && this.feedback.textContent !== text) this.feedback.textContent = text; }
    setReason(reason) {
      if (!this.review || !this.status || reason === this.lastReason) return;
      const messages = {
        target: 'Automatic display is restricted to the verified anniversary theme.',
        auto_off: 'Automatic trigger is OFF in this draft’s popup settings.',
        unreviewed: 'Automatic display is locked until shared popup conflicts have been verified.',
        privacy_loading: 'Waiting for Shopify’s existing privacy API to load. No consent is being requested or changed.',
        privacy_missing: 'Shopify’s privacy API is unavailable. Automatic display requires a reliable existing privacy decision.',
        privacy_load_failed: 'Shopify’s privacy API failed to load. Automatic display remains paused.',
        privacy_error: 'The existing privacy decision could not be read. Automatic display remains paused.',
        privacy_denied: 'Existing privacy settings do not allow preference storage. Manual review remains available.',
        storage_failed: 'Popup frequency storage is unavailable or failed. Automatic display remains paused.',
        excluded: 'This page is excluded from automatic display, including product, cart, account and main-form pages.',
        schedule: 'The display schedule is outside its window or contains an invalid date.',
        hidden: 'Timer paused: this browser tab is not visible.',
        unfocused: 'Timer paused: this page does not have focus.',
        overlay: 'Timer paused: another popup, cart, search or privacy overlay is open.',
        scroll_lock: 'Timer paused: another feature is locking page scrolling.',
        input: 'Timer paused: an input is focused or was used within the last 15 seconds.',
        blocked: 'Automatic display is temporarily blocked by page interaction.',
        checkout: 'Checkout was started. Popup display remains paused for this visit.',
        shown: 'This visit has already shown the popup. Refreshing does not reset the visit limit.',
        cooldown: 'The close or CTA cooldown is still active. Manual review can bypass this popup’s frequency limit.',
        short_page: 'This page is too short for meaningful scrolling. Use the manual review entry.',
        waiting_both: 'Waiting for both active viewing time and the scroll threshold.',
        waiting_time: 'Scroll threshold reached. Waiting for active viewing time.',
        waiting_time_only: 'Waiting for active viewing time. Scrolling is not required.',
        waiting_scroll: 'Active viewing time reached. Waiting for the scroll threshold.',
        ready: 'Active viewing and all required automatic display conditions are satisfied.',
        open: 'Popup is open. Automatic display is paused while it is open.'
      };
      this.lastReason = reason; this.status.dataset.reason = reason;
      this.status.textContent = messages[reason] || messages.blocked;
    }
    diagnose(snapshot) {
      if (!this.review) return;
      if (this.progress) {
        const ratio = Math.max(0, Math.min(1, snapshot.scrollRatio));
        const scroll = snapshot.requireScroll === false ? 'not required' : Math.round(ratio * 100) + '% / ' + snapshot.threshold + '%';
        const text = 'Active viewing: ' + Math.floor(snapshot.activeMs / 1000) + ' / ' + snapshot.delay + ' s · Scroll: ' + scroll;
        if (this.progress.textContent !== text) this.progress.textContent = text;
      }
      this.setReason(rules.autoReason(snapshot));
    }
    isInput(node) { return node instanceof Element && !!node.closest('input,textarea,select,[contenteditable="true"],[role="combobox"]'); }
    visible(node) {
      // Closed details can leave a non-zero border rect on their hidden content.
      // Only the first direct summary (and its descendants) remains visible.
      for (let ancestor = node.parentElement; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.tagName === 'DETAILS' && !ancestor.open) {
          const summary = [...ancestor.children].find(child => child.tagName === 'SUMMARY');
          if (!summary?.contains(node)) return false;
        }
      }
      const style = getComputedStyle(node);
      return !node.hidden && !node.closest('[hidden],[inert],[aria-hidden="true"]') && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 &&
        [...node.getClientRects()].some(rect => rect.width > 0 && rect.height > 0);
    }
    overlay() {
      const selectors = [
        'dialog[open]', '[role="dialog"]', '[aria-modal="true"]', 'modal-dialog[open]',
        'cart-drawer.active', '#CartDrawer.active', '.drawer.active', '.drawer.is-open',
        'details-modal details[open]', 'predictive-search[open]', '.search-modal[open]',
        'menu-drawer details[open]', 'quick-add-modal[open]', '.fa-spec-dialog[open]',
        '[class*="klaviyo-form"]', '[id*="omnisend-form"]', '[id*="optimonk"]',
        '[id*="privy"]', '[class*="kaching-cart"][class*="open"]', '[data-fp-external-modal="open"]',
        'iframe[title*="popup" i]', 'iframe[title*="subscribe" i]', '#shopify-pc__banner'
      ];
      return [...document.querySelectorAll(selectors.join(','))].some(node => !this.contains(node) && this.visible(node));
    }
    blocked(includeInput = true) {
      return !!this.blockReason(includeInput);
    }
    blockReason(includeInput = true) {
      if (document.hidden) return 'hidden';
      if (!document.hasFocus()) return 'unfocused';
      if (this.overlay()) return 'overlay';
      if (!this.dialog.open && (/(hidden|clip)/.test(getComputedStyle(document.body).overflowY) || /overflow-hidden/.test(document.body.className))) return 'scroll_lock';
      if (includeInput && !this.contains(document.activeElement) && (this.isInput(document.activeElement) || Date.now() < this.inputUntil)) return 'input';
      return '';
    }
    storage() {
      const privacy = window.Shopify.customerPrivacy;
      this.storageReason = this.privacyLoadError ? 'privacy_load_failed' : this.privacyLoading ? 'privacy_loading' : 'privacy_missing';
      if (typeof privacy?.preferencesProcessingAllowed !== 'function') return null;
      try {
        this.storageReason = 'privacy_error';
        if (privacy.preferencesProcessingAllowed() !== true) { this.storageReason = 'privacy_denied'; return null; }
        this.storageReason = 'storage_failed';
        const storage = window.localStorage;
        const raw = storage.getItem(KEY);
        // Validate writing before relying on a cross-page frequency guarantee.
        if (raw === null) { storage.setItem(KEY, '{}'); storage.removeItem(KEY); }
        else storage.setItem(KEY, raw);
        this.storageReason = ''; return storage;
      } catch (_) { return null; }
    }
    read(storage) { return rules.state(storage?.getItem(KEY), Date.now()); }
    write(storage, data) { try { storage?.setItem(KEY, JSON.stringify(data)); return !!storage; } catch (_) { this.storageReason = 'storage_failed'; return false; } }
    pageAllowed() {
      if (!['index', 'collection', 'page', 'blog', 'article'].includes(this.dataset.page)) return false;
      if (/\/(cart|account|checkouts?|challenge|password)(\/|$)/i.test(location.pathname)) return false;
      // Ordinary content pages with a main form are intentionally manual-only.
      if (this.dataset.page === 'page' && document.querySelector('main form')) return false;
      return true;
    }
    kind(requested) {
      if (kinds.includes(requested)) return requested;
      if (kinds.includes(this.dataset.mode)) return this.dataset.mode;
      if (this.dataset.page === 'index') {
        for (const [id, kind] of [['anniversary-restock', 'pro'], ['anniversary-diy', 'diy']]) {
          const area = document.getElementById(id);
          if (area) { const r = area.getBoundingClientRect(); if (r.top < innerHeight * .6 && r.bottom > innerHeight * .4) return kind; }
        }
      }
      return kinds.includes(this.dataset.context) ? this.dataset.context : 'general';
    }
    pulse() {
      if (!window.Shopify?.designMode && !rules.inWindow(this.dataset.start, this.dataset.end, Date.now())) {
        if (this.dialog.open) this.close('schedule');
        this.setReason('schedule');
        return;
      }
      const tick = performance.now();
      const elapsed = Math.min(1500, Math.max(0, tick - this.lastTick)); this.lastTick = tick;
      if (this.dialog.open) {
        if (this.overlay() || this.checkingOut) this.close('conflict');
        if (this.dialog.open) this.setReason('open');
        return;
      }
      const blockReason = this.blockReason();
      const blocked = !!blockReason;
      // A form return can bypass our frequency limit, but not another dialog or checkout.
      if (this.emailReturnKind && !this.emailReturnHandled && !blocked) {
        this.open(this.emailReturnKind, null);
        if (this.dialog.open) {
          this.emailReturnHandled = true;
          const feedback = this.dialog.querySelector('[data-fp-email-feedback]') || this.dialog.querySelector('[data-fp-email-return-note]');
          if (feedback) { feedback.hidden = false; feedback.focus({ preventScroll: false }); }
          this.consumeEmailReturn();
          return;
        }
      }
      if (!blocked) this.activeMs += elapsed;
      let storage = this.storage();
      let state; try { state = this.read(storage); } catch (_) {
        storage = null; this.storageReason = 'storage_failed'; state = rules.state(null, Date.now());
      }
      if (storage && Date.now() - this.lastTouch > 10000 && !document.hidden) {
        if (!this.write(storage, state)) storage = null;
        this.lastTouch = Date.now();
      }
      const range = document.documentElement.scrollHeight - innerHeight;
      const snapshot = { target: String(window.Shopify?.theme?.id) === TARGET,
        enabled: this.dataset.auto === 'true', reviewed: this.dataset.reviewed === 'true', storage: !!storage,
        storageReason: this.storageReason, blockReason,
        pageAllowed: this.pageAllowed(), inWindow: rules.inWindow(this.dataset.start, this.dataset.end, Date.now()),
        blocked, checkout: this.checkingOut || state.checkoutUntil > Date.now(), shown: this.pageShown || state.shown,
        cooling: state.closedUntil > Date.now(), activeMs: this.activeMs, delay: Number(this.dataset.delay),
        requireScroll: this.dataset.scrollRequired !== 'false',
        scrollRange: range, height: innerHeight, scrollRatio: range > 0 ? scrollY / range : 0, threshold: Number(this.dataset.scroll) };
      this.diagnose(snapshot);
      if (rules.canAuto(snapshot)) this.open('auto', null, true);
    }
    open(requested, opener, automatic = false) {
      if (this.dialog.open || this.checkingOut) return;
      if (!window.Shopify?.designMode && !rules.inWindow(this.dataset.start, this.dataset.end, Date.now())) {
        this.setReason('schedule');
        return;
      }
      if (this.blocked(automatic)) { this.setStatus('Close the cart, search or other popup before reviewing.'); return; }
      const storage = this.storage();
      let state; try { state = this.read(storage); } catch (_) { state = rules.state(null, Date.now()); }
      if (state.checkoutUntil > Date.now()) { this.setStatus('Checkout was started. Automatic and manual display are paused for this visit.'); return; }
      if (automatic && (!storage || state.shown || state.closedUntil > Date.now() || this.pageShown)) return;
      const kind = this.kind(requested);
      const template = this.querySelector('template[data-fp-panel="' + kind + '"]');
      if (!template) return;
      this.querySelector('[data-fp-content]').replaceChildren(template.content.cloneNode(true));
      const cta = this.querySelector('[data-fp-cta]');
      const safe = rules.safeHref(cta.getAttribute('href'), location.origin, window.Shopify.routes?.root || '/');
      if (!safe) { this.setStatus('Destination blocked. Choose an existing internal page, collection or anchor, without discount links.'); return; }
      cta.href = safe;
      const signup = this.querySelector('[data-fp-signup]');
      if (signup) {
        const redirect = rules.emailReturn(location.href, kind, signup.id, location.origin, window.Shopify.routes?.root || '/');
        const returnInput = signup.querySelector('[name="return_to"]');
        if (!redirect || !returnInput) return;
        returnInput.value = redirect;
        // Preserve Liquid's native POST action byte-for-byte. A preview_theme_id query on
        // /contact can invoke a preview redirect before the subscription POST is handled.
        // Carry preview context only in return_to; validation uses the existing preview cookie.
        const action = new URL(signup.action, location.origin);
        if (action.origin !== location.origin || !/\/contact$/.test(action.pathname)) return;
      }
      // Persist before display. If writing fails, automatic display remains disabled.
      state.shown = true;
      if (automatic && !this.write(storage, state)) { this.setReason('storage_failed'); return; }
      if (!automatic) this.write(storage, state);
      this.pageShown = true; this.opener = opener || document.activeElement;
      this.lockOwned = !document.documentElement.classList.contains('fp-scroll-lock');
      document.documentElement.classList.add('fp-scroll-lock');
      try {
        this.dialog.showModal(); this.querySelector('[data-fp-close]').focus({ preventScroll: true }); this.setReason('open');
        if (signup) this.protectEmailForm(signup);
      }
      catch (_) { this.release(); this.setStatus('Dialog unavailable in this browser. Automatic display stopped.'); }
    }
    close(reason) {
      if (!this.dialog?.open) return;
      if (reason === 'dismiss' || reason === 'cta') {
        const storage = this.storage();
        try { const state = this.read(storage); state.shown = true; state.closedUntil = Date.now() + Number(this.dataset.cooldown) * DAY; this.write(storage, state); } catch (_) { /* In-memory page guard remains. */ }
      }
      this.dialog.close(); this.release();
    }
    release() {
      if (this.lockOwned) document.documentElement.classList.remove('fp-scroll-lock');
      this.lockOwned = false;
      if (this.opener?.isConnected && !this.overlay()) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
    trap(e) {
      if (e.key !== 'Tab') return;
      const nodes = [...this.dialog.querySelectorAll('button,a[href],input:not([type="hidden"]),select,textarea,[tabindex]')].filter(n => !n.disabled && n.tabIndex >= 0 && this.visible(n));
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    protectEmailForm(form) {
      if (form.dataset.fpCaptchaBound === 'true' || typeof window.Shopify.captcha?.protect !== 'function') return;
      try {
        // Supported Shopify hook for a form inserted after DOMContentLoaded.
        window.Shopify.captcha.protect(form);
        form.dataset.fpCaptchaBound = 'true';
      } catch (_) { /* Native interaction wiring still applies; never bypass CAPTCHA. */ }
    }
    consumeEmailReturn() {
      // Consume only our one-time response marker; retain the theme and review parameters.
      const url = new URL(location.href);
      url.searchParams.delete('fadlash_email_return');
      const form = this.dialog.querySelector('[data-fp-signup]');
      if (form && url.hash === '#' + form.id) url.hash = '';
      try { history.replaceState(history.state, '', url.pathname + url.search + url.hash); }
      catch (_) { /* In-memory handled flag still prevents a repeat on this page. */ }
    }
    stopCheckout() {
      this.checkingOut = true;
      const storage = this.storage();
      try { const state = this.read(storage); state.checkoutUntil = Date.now() + VISIT; this.write(storage, state); } catch (_) { /* page flag remains */ }
      this.close('checkout');
    }
    reset() {
      // Never clear all storage, Shopify cookies, cart or another app's data.
      if (this.dialog.open) this.close('reset');
      try {
        const storage = this.storage();
        const state = this.read(storage);
        if (state.checkoutUntil > Date.now()) {
          storage?.setItem(KEY, JSON.stringify({ checkoutUntil: state.checkoutUntil }));
          this.checkingOut = true;
        } else window.localStorage.removeItem(KEY);
      } catch (_) { /* No storage to reset. */ }
      this.pageShown = false; this.activeMs = 0; this.lastTick = performance.now();
      // A checkout stop cannot be bypassed by a review reset.
      this.setStatus('Only this popup’s frequency record was removed. Timer restarted; privacy and other app data are unchanged.');
    }
  }
  customElements.define('fadlash-promo-popup', FadlashPromoPopup);
})();
